import { FormGroup, Validators } from '@angular/forms';
import {
  clearNotbankBankValidators,
  isNotbankBankSelected,
  updateNotbankBankValidators,
} from '../notbank-cashout.logic';
import {
  extractBankCode,
  extractBankName,
  normalizeText,
  parseMetadata,
} from '../cashout-bank.helpers';

/** Canales Minteo para payouts COP (BREB = llaves; SAVE/TURBO = cuenta bancaria). */
export const MINTEO_COP_CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'SAVE', label: 'SAVE (transferencia estándar, mín. 10.000 COP)' },
  { value: 'TURBO', label: 'TURBO (rápida, mín. 10.000 COP)' },
  {
    value: 'BREB',
    label: 'BREB (llave BreB, mín. 100 COP, máx. 11.500.000 COP)',
  },
];

export const BREB_KEY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALPHANUMERIC', label: 'ALPHANUMERIC (@...)' },
  { value: 'PHONE', label: 'PHONE (10 dígitos)' },
  { value: 'EMAIL', label: 'EMAIL' },
  { value: 'ID', label: 'ID (documento)' },
];

/** Wallets Minteo para cashouts COP (origen del payout). */
export const MINTEO_ORIGIN_WALLETS: { id: string; label: string }[] = [
  {
    id: '0x635230Fc636288f500EeE8438793e400EeAc05A0',
    label: 'Wallet swappers Phantom (0x6352...Ac05A0)',
  },
  {
    id: '0xb986778951A9F03AFE258585Ec40D61d42A5fa7D',
    label: 'Wallet Littio (0xb986...A5fa7D)',
  },
  {
    id: '0xb592E5763da387a10EE73A9624977f39D23230b9',
    label: 'Wallet Finity (0xb592...3230b9)',
  },
];

export function setupCopCashoutForm(ctx: any): void {
  clearCopMinteoUiIfNotMinteoBank(ctx);
  clearCopBitsoBeneficiaryIfNotBitso(ctx);
  updateCopMinteoValidators(ctx);
  updateCopBitsoValidators(ctx);
}

/** COP: proveedor de payout Bitso (subwallet / código BITSO_COP). */
export function isCopBitsoBankSelected(ctx: any): boolean {
  const bank = ctx.newTransactionForm.controls.company_id.value;
  const code = (extractBankCode(bank) ?? '').toString().trim().toUpperCase();

  if (
    code === 'BITSO_COP' ||
    code === 'BITSO_CO' ||
    code.startsWith('BITSO_COP') ||
    (code.startsWith('BITSO_') && code.includes('COP'))
  ) {
    return true;
  }

  if (typeof bank === 'object' && bank) {
    const metadata = parseMetadata(bank.metadata);
    const provider = String(metadata?.['provider'] ?? '')
      .trim()
      .toLowerCase();
    if (provider === 'bitso') {
      return true;
    }
  }

  const bankName = extractBankName(bank);
  return !!bankName && normalizeText(bankName).includes('bitso');
}

/** Bancos colombianos del beneficiario (excluye rails Minteo/Bitso). */
export function getCopBeneficiaryBankOptions(ctx: any): any[] {
  const banks = ctx.getBanks?.() ?? [];
  return banks.filter((b: any) => {
    const code = (extractBankCode(b) ?? '').toString().trim().toUpperCase();
    if (!code) {
      return false;
    }
    if (code.includes('MINTEO') || code.startsWith('BITSO')) {
      return false;
    }
    return true;
  });
}

export function clearCopBitsoBeneficiaryIfNotBitso(ctx: any): void {
  if (isCopBitsoBankSelected(ctx)) {
    return;
  }
  ctx.newTransactionForm.patchValue(
    { cop_beneficiary_bank: null },
    { emitEvent: false },
  );
}

export function updateCopBitsoValidators(ctx: any): void {
  const ben = ctx.newTransactionForm.get('cop_beneficiary_bank');
  if (!ben) {
    return;
  }
  if (isCopBitsoBankSelected(ctx)) {
    ben.setValidators([Validators.required]);
  } else {
    ben.clearValidators();
  }
  ben.updateValueAndValidity({ emitEvent: false });
}

export function onCopBankPicked(ctx: any): void {
  clearCopMinteoUiIfNotMinteoBank(ctx);
  clearCopBitsoBeneficiaryIfNotBitso(ctx);
  updateCopMinteoValidators(ctx);
  updateCopBitsoValidators(ctx);
}

/** COP: true si el subwallet/banco elegido es Minteo (p. ej. MINTEO_COP). */
export function isCopMinteoBankSelected(ctx: any): boolean {
  const bank = ctx.newTransactionForm.controls.company_id.value;
  const code = (extractBankCode(bank) ?? '').toString().trim().toUpperCase();

  // En configs antiguas el id/code puede variar (MINTEO, MINTEO_CO, etc.).
  if (
    code === 'MINTEO_COP' ||
    code === 'MINTEO' ||
    code.startsWith('MINTEO_') ||
    code.includes('MINTEO')
  ) {
    return true;
  }

  if (typeof bank === 'object' && bank) {
    const metadata = parseMetadata(bank.metadata);
    const provider = String(metadata?.['provider'] ?? '')
      .trim()
      .toLowerCase();
    if (provider === 'minteo') {
      return true;
    }
  }

  const bankName = extractBankName(bank);
  return !!bankName && normalizeText(bankName).includes('minteo');
}

/** Limpia canal/BreB al salir de Minteo para evitar estado oculto inválido. */
export function clearCopMinteoUiIfNotMinteoBank(ctx: any): void {
  if (isCopMinteoBankSelected(ctx)) {
    return;
  }

  ctx.newTransactionForm.patchValue(
    {
      minteo_channel: 'SAVE',
      breb_key_type: null,
      breb_key_value: '',
    },
    { emitEvent: false },
  );
}

/**
 * Validación dinámica COP: SAVE/TURBO requieren banco + número de cuenta;
 * BREB requiere tipo y valor de llave; Notbank usa notbank_bank (sin account_number).
 */
export function updateCopMinteoValidators(ctx: any): void {
  const g = ctx.newTransactionForm;
  const company = g.get('company_id');
  const accountNum = g.get('account_number');
  const brebT = g.get('breb_key_type');
  const brebV = g.get('breb_key_value');
  const nb = g.get('notbank_bank');

  if (isNotbankBankSelected(ctx)) {
    company?.setValidators([Validators.required]);
    accountNum?.clearValidators();
    brebT?.clearValidators();
    brebV?.clearValidators();
    if (nb instanceof FormGroup) {
      updateNotbankBankValidators(nb, 'COP');
    }
    [company, accountNum, brebT, brebV].forEach((c) =>
      c?.updateValueAndValidity({ emitEvent: false }),
    );
    return;
  }

  if (nb instanceof FormGroup) {
    clearNotbankBankValidators(nb);
  }

  if (!isCopMinteoBankSelected(ctx) || isCopBitsoBankSelected(ctx)) {
    company?.setValidators([Validators.required]);
    accountNum?.setValidators([Validators.required]);
    brebT?.clearValidators();
    brebV?.clearValidators();
    [company, accountNum, brebT, brebV].forEach((c) =>
      c?.updateValueAndValidity({ emitEvent: false }),
    );
    return;
  }

  const channel = (g.get('minteo_channel')?.value ?? 'SAVE')
    .toString()
    .toUpperCase();
  if (channel === 'BREB') {
    company?.clearValidators();
    accountNum?.clearValidators();
    brebT?.setValidators([Validators.required]);
    brebV?.setValidators([Validators.required]);
  } else {
    company?.setValidators([Validators.required]);
    accountNum?.setValidators([Validators.required]);
    brebT?.clearValidators();
    brebV?.clearValidators();
  }
  [company, accountNum, brebT, brebV].forEach((c) =>
    c?.updateValueAndValidity({ emitEvent: false }),
  );
}

/** Minteo espera SAVINGS | CHECKING en channel_data, no nombres en español. */
export function mapCopLocalAccountTypeToMinteo(
  local: string | null | undefined,
): string {
  const t = (local ?? '').toString();
  if (t === 'Cuenta Corriente') {
    return 'CHECKING';
  }
  if (t === 'Cuenta de Ahorros') {
    return 'SAVINGS';
  }
  return 'SAVINGS';
}

export function isCopDocumentMissing(ctx: any): boolean {
  const value = ctx.newTransactionForm.controls.document_type_number.value;
  return !(value ?? '').toString().trim();
}
